<template>
  <Dialog
    :visible="internalVisible"
    modal
    header="Scan Karaoke Signup QR Code"
    :style="{ width: '600px' }"
    class="qr-scanner-modal"
    @update:visible="handleUpdateVisible"
  >
    <div class="p-fluid">
      <!-- Scanner Container -->
      <div v-if="!scanResult" class="space-y-4">
        <div ref="scannerContainer" id="qr-code-reader" class="bg-black rounded-lg overflow-hidden">
          <div v-if="scanning" class="text-center py-8">
            <i class="pi pi-spinner pi-spin text-4xl text-white mb-2"></i>
            <p class="text-white text-sm">Scanning camera...</p>
          </div>
          
          <div v-else-if="error" class="text-center py-8">
            <i class="pi pi-exclamation-triangle text-4xl text-red-400 mb-2"></i>
            <p class="text-red-400 text-sm">{{ error }}</p>
            <button
              @click="startScanner"
              class="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              Retry
            </button>
          </div>
        </div>

        <div class="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p class="text-amber-800 dark:text-amber-200 text-xs leading-relaxed m-0">
            <i class="pi pi-info-circle mr-1"></i>
            Position your camera so the QR code (from performer) fills the frame. The code contains performer name and song info.
          </p>
        </div>
      </div>

      <!-- Result Display -->
      <div v-else class="space-y-4">
        <div class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <h4 class="text-base font-semibold text-green-900 dark:text-green-200 m-0 mb-2">
            ✓ QR Code Scanned Successfully
          </h4>
          <p class="text-sm text-gray-600 dark:text-gray-400 m-0">
            Token verified. Confirm this signup below.
          </p>
        </div>

        <!-- Verification Details -->
        <div class="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg space-y-2">
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Song:</span>
            <span class="text-sm font-bold text-gray-900 dark:text-white">{{ result?.songTitle || "Unknown" }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Artist:</span>
            <span class="text-sm text-gray-900 dark:text-white">{{ result?.artist || "-" }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Performer:</span>
            <span class="text-sm font-bold text-blue-600 dark:text-blue-400">{{ result?.performerName || "Unknown" }}</span>
          </div>
          <div v-if="result?.expiresAt" class="flex justify-between items-center">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Expires:</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatExpiresAt(result.expiresAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button 
        v-if="!scanResult" 
        label="Cancel" 
        severity="secondary" 
        @click="closeScanner" 
      />
      <template v-else>
        <Button 
          label="Reject" 
          severity="danger" 
          @click="rejectSignup"
        />
        <Button 
          label="Accept & Add to Queue" 
          icon="pi pi-check" 
          severity="success" 
          @click="acceptSignup" 
          :disabled="accepting"
        />
      </template>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useToast } from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import api from "@/services/api";
import { Html5Qrcode } from "html5-qrcode";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  eventId: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(["update:visible", "accepted", "rejected"]);

const toast = useToast();

// State
const scannerContainer = ref(null);
const scanning = ref(false);
const error = ref("");
const scanResult = ref(null);
const result = ref(null);
const accepting = ref(false);
let html5QrCode = null;

// Local state for dialog visibility
const internalVisible = ref(props.visible);

// Watchers
watch(() => props.visible, async (newVal) => {
  if (newVal) {
    startScanner();
    internalVisible.value = true;
  } else {
    closeScanner();
  }
});

// Methods
const handleUpdateVisible = (value) => {
  internalVisible.value = value;
  emit("update:visible", value);
};

const startScanner = async () => {
  scanning.value = true;
  error.value = "";
  scanResult.value = null;

  try {
    // Ensure scanner container is mounted before starting camera
    await nextTick();

    // Create new Html5Qrcode instance (not QRCodeScannerLibrary)
    html5QrCode = new Html5Qrcode(scannerContainer.value.id || 'qr-code-reader');

    await html5QrCode.start(
      { facingMode: "environment" }, // Prefer back camera
      {
        fps: 10,
        qrbox: { width: 250, height: 250 }
      },
      (decodedText, decodedResult) => {
        // QR code scanned successfully
        handleScanSuccess(decodedText);
      },
      (errorMessage) => {
        // Scan in progress but no code yet - ignore errors during scanning
        console.log("Scanning...", errorMessage);
      }
    );

    scanning.value = false;
  } catch (err) {
    console.error("Failed to start scanner:", err);
    scanning.value = false;
    error.value = "Could not access camera. Please ensure you have granted camera permissions.";
  }
};

const handleScanSuccess = async (qrCodeText) => {
  if (scanResult.value || accepting.value) return;

  try {
    scanning.value = false;
    
    // Stop scanner after successful scan
    await stopScanner();

    // Verify token with backend
    const response = await api.post("/api/karaoke/signup/verify", {
      token: qrCodeText
    });

    if (response.data.success) {
      result.value = response.data.data;
      scanResult.value = qrCodeText;

      toast.add({
        severity: "info",
        summary: "QR Code Verified",
        detail: `Performer: ${result.value.performerName} - Song: ${result.value.songTitle}`,
        life: 3000
      });
    } else {
      throw new Error(response.data.message || "Token verification failed");
    }
  } catch (err) {
    console.error("Verification failed:", err);
    
    if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else {
      error.value = "Invalid or expired QR code";
    }

    scanResult.value = null;
    
    // Restart scanner after 2 seconds
    setTimeout(() => {
      startScanner();
    }, 2000);
  }
};

const acceptSignup = async () => {
  if (!result.value || accepting.value) return;

  // Security: Verify scan is for current Live event
  if (result.value.eventId !== props.eventId) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "QR code from different event - ignoring",
      life: 3000
    });
    return;
  }

  accepting.value = true;

  try {
    emit("accepted", {
      eventId: result.value.eventId,
      songId: result.value.songId,
      performerName: result.value.performerName,
      songTitle: result.value.songTitle
    });

    await closeScanner();
  } finally {
    accepting.value = false;
  }
};

const rejectSignup = () => {
  toast.add({
    severity: "warn",
    summary: "Rejected",
    detail: "Karaoke signup rejected",
    life: 3000
  });

  emit("rejected");
  closeScanner();
};

const resetScannerState = () => {
  scanning.value = false;
  error.value = "";
  scanResult.value = null;
  result.value = null;
  accepting.value = false;
};

const closeScanner = async () => {
  await stopScanner();
  resetScannerState();
  internalVisible.value = false;
  emit("update:visible", false);
};

const stopScanner = async () => {
  if (html5QrCode) {
    try {
      await html5QrCode.stop();
      await html5QrCode.clear();
    } catch (err) {
      console.warn("Error stopping scanner:", err);
    }
    html5QrCode = null;
  }
};

const formatExpiresAt = (expiresAtStr) => {
  try {
    const date = new Date(expiresAtStr);
    return date.toLocaleString();
  } catch (err) {
    return expiresAtStr;
  }
};

onUnmounted(() => {
  stopScanner();
});
</script>

<style scoped>
.qr-scanner-modal :deep(.p-dialog-header) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.qr-scanner-modal :deep(.p-dialog-content) {
  max-height: 70vh;
  overflow-y: auto;
}

/* Prevent bottom margin on last child */
.qr-scanner-modal :deep(.p-fluid > div:last-child) {
  margin-bottom: 0;
}
</style>
